import * as React from 'react';
import TopBar from '../components/molecules/TopBar';
import * as _ from 'underscore';
import TopBarHomeButton from '../components/atoms/TopBarHomeButton';
import TopBarBackButton from '../components/atoms/TopBarBackButton';
import { Route, Routes, To, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModuleStateDB } from '../data/datatypes';
import { ModuleDataDB } from '@app/common';
import { checkTutorialModulesDone, getInitialModuleState, getModule, getModuleState } from '../services/DBService/DbService';
import SlotOverview from '../components/organisms/Modules/Slots/SlotOverview';
import Session from '../components/organisms/Sessions/Session';
import Section from '../components/organisms/Section/Section';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import TopBarFavoriteButton from '../components/atoms/TopBarFavoriteButton';
import { setCurrentExerciseUrl as setCurrentExerciseUrl, setCurrentModuleState } from '../store/moduleStateSlice';
import { createAndSaveEvent } from '../services/EventLoggingService/EventLoggingService';
import { setDoTutorial } from '../store/globalStateSlice';
import { cancelNotificationForModule } from '../services/CordovaService/Notifications';
import LoadingScreen from '../components/atoms/LoadingScreen';
import { getNextVisibleSessionUrl, getSkipToUrlForSingleItems, getSkipToUrlForAutoSkip, getSkipToUrlForStartSectionSelection } from 'src/services/DecisionTreeService/DecisionTreeService';
import CompletionHeader from '../components/molecules/CompletionHeader';
import { isModuleFullyCompletedBySlot } from '../utils/moduleCompletion';
import { store } from '../store/store';
import { isModuleSlotOverviewPath, resolveBypassSlotOverview } from './Module.logic';

const Module = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const transientStateProperties = useAppSelector(state => state.moduleState.transientStateProperties);

    const moduleId = params.moduleId as `module_${string}`;
    const [module, setModule] = React.useState<ModuleDataDB>();
    const moduleState = useAppSelector(state => state.moduleState.currentModuleState);
    const userProperties = useAppSelector(state => state.globalState.user?.userProperties);
    const config = useAppSelector(state => state.globalState.config);
    const user = useAppSelector(state => state.globalState.user);
    const [lastOpenedAt] = React.useState<Date>(new Date());
    const bypassSlotOverview = resolveBypassSlotOverview(
        module?.bypassSlotOverview,
        config?.bypassSlotOverview
    );
    
    // Calculate completion status directly without effect to avoid infinite loop
    const isCompleted = React.useMemo(() => {
        if (!module || !moduleState) {
            return false;
        }
        return isModuleFullyCompletedBySlot(
            module,
            moduleState,
            userProperties || [],
            config?.enableTransientSessionModes === true
        );
    }, [module, moduleState?._id, moduleState?.finished, userProperties, config?.enableTransientSessionModes]);

    React.useEffect(() => {
        if (!moduleId?.startsWith('module_')) {
            throw new Error(`ModuleID incorrect: ${moduleId}`);
        }
        const fetchModule = async () => {
            const data = await getModule(moduleId);
            const state = await getModuleState(moduleId);
            setModule(data);
            if (!state) {
                dispatch(setCurrentModuleState(getInitialModuleState(moduleId)));
            } else {
                dispatch(setCurrentModuleState(state));
            }
        };
        fetchModule();
    }, [moduleId]);

    React.useEffect(() => {
        if (
            module &&
            bypassSlotOverview &&
            moduleState?._id === moduleId &&
            isModuleSlotOverviewPath(location.pathname, moduleId)
        ) {
            const _skipToUrlByConfig = getSkipToUrlForAutoSkip(module, userProperties || [], moduleState);
            if (_skipToUrlByConfig) {
                navigate(`slots/${_skipToUrlByConfig}`, { replace: true });
            }
        }
    }, [
        module,
        moduleId,
        moduleState,
        userProperties,
        bypassSlotOverview,
        location.pathname,
        navigate
    ]);

    React.useEffect(() => {
        if (module) {
            const _skipToUrlByModule = getSkipToUrlForSingleItems(module, userProperties || [], moduleState);
            if (_skipToUrlByModule) {
                navigate(`slots/${_skipToUrlByModule}`, { replace: true });
            }
        }
    }, [module, config?.bypassSlotOverview]);

    const leftTopBarButtons = (navigateBackPath: To) => [
        <TopBarHomeButton />,
        <TopBarBackButton onClick={() => navigate(navigateBackPath)} />
    ];

    const addToFinishedObject = (value: `slot_${string}__session_${string}` | 'start' | 'end'): ModuleStateDB => {
        const latestModuleState = store.getState().moduleState.currentModuleState;
        let newState: ModuleStateDB = {
            ...latestModuleState,
            _id: moduleId
        };

        if (value === 'start' || value === 'end') {
            newState.finished = {
                ...newState.finished,
                [value]: true
            };
        } else {
            if (config?.enableTransientSessionModes) {
                const combined = [...(newState.finished[value] || []), ...transientStateProperties];
                const newProperties = Array.from(new Set(combined));
                newState.finished = {
                    ...newState.finished,
                    [value]: newProperties
                };
            } else {
                newState.finished = {
                    ...newState.finished,
                    [value]: true
                };
            }
        }
        dispatch(setCurrentModuleState(newState));
        createAndSaveEvent(user?._id, 'session_finished', `${moduleId}: ${value}`);
        return newState;
    };

    const startSectionFinished = () => {
        const updatedModuleState = addToFinishedObject('start');
        dispatch(setCurrentExerciseUrl(undefined));
        if (module) {
            const skipToUrl = getSkipToUrlForStartSectionSelection(module, userProperties || [], updatedModuleState);
            if (skipToUrl) {
                navigate(`slots/${skipToUrl}`);
                return;
            }
        }
        navigate('slots');
    };

    const endSectionFinished = async () => {
        addToFinishedObject('end');
        dispatch(setCurrentExerciseUrl(undefined));

        // // special case: tutorial is done once 1 module is finished
        // dispatch(setDoTutorial(false));
        // changed to: leave tutorial mode if first two modules are finished
        if (userProperties && userProperties.includes('DO_TUTORIAL')) {
            const tutorialsDone = await checkTutorialModulesDone(moduleId);
            dispatch(setDoTutorial(!tutorialsDone));
        }

        navigate('/');
    };

    const sessionFinished = (session?: `slot_${string}__session_${string}`) => {
        if (!session) {
            return;
        }
        const updatedModuleState = addToFinishedObject(session);
        dispatch(setCurrentExerciseUrl(undefined));
        
        // Check if module is now fully completed and cancel notifications if so
        const latestModuleState = store.getState().moduleState.currentModuleState;
        if (module && latestModuleState && userProperties) {
            // We need to check completion with the updated state
            // Create a temporary updated state to check completion
            const updatedState = {
                ...latestModuleState,
                finished: {
                    ...latestModuleState.finished,
                    [session]: config?.enableTransientSessionModes ? 
                        [...(latestModuleState.finished[session] || []), ...transientStateProperties] : 
                        true
                }
            };
            
            const moduleNowCompleted = isModuleFullyCompletedBySlot(
                module,
                updatedState,
                userProperties,
                config?.enableTransientSessionModes === true
            );
            
            if (moduleNowCompleted) {
                cancelNotificationForModule(moduleId);
            }
        }
        
        if (bypassSlotOverview) {
            const [slotId, sessionId] = session.split('__') as [`slot_${string}`, `session_${string}`];
            const nextSessionUrl = module && getNextVisibleSessionUrl(
                module,
                slotId,
                sessionId,
                userProperties || [],
                updatedModuleState
            );
            if (nextSessionUrl) {
                navigate(`slots/${nextSessionUrl}`);
                return;
            }
            if (module?.endSection) {
                navigate('end');
                return;
            }
            navigate('/modules');
        } else {
            navigate('slots');
        }
    };

    const getBackButtonUrl = (): To => {
        return -1 as To;
    };

    let title = '';
    let content: JSX.Element;
    if (module !== undefined) {
        title = module.name;
        content = (
            <Routes>
                <Route
                    path="slots/:slotId/:sessionId/:sectionId/*"
                    element={
                        <div className={`content module ${isCompleted ? 'has-completion-header' : ''}`}>
                            <TopBar
                                title={title}
                                leftButtons={leftTopBarButtons(getBackButtonUrl())}
                                rightButtons={[
                                    <TopBarFavoriteButton />
                                ]}
                            />
                            <CompletionHeader isCompleted={isCompleted} lastOpenedAt={lastOpenedAt} />
                            <div className="content-container content-module">
                                {<Session
                                    module={module}
                                    sessionFinished={sessionFinished}
                                />}
                            </div>
                        </div>
                    }
                />

                <Route
                    path="*"
                    element={
                        <div className={`content module ${isCompleted ? 'has-completion-header' : ''}`}>
                            <TopBar
                                title={title}
                                leftButtons={leftTopBarButtons(`/modules?scrollTo=${moduleId}`)}
                            />
                            <CompletionHeader isCompleted={isCompleted} lastOpenedAt={lastOpenedAt} />
                            <div className="content-container content-module">
                                <Routes>
                                    <Route path="start">
                                        <Route
                                            path=""
                                            element={<Section
                                                sectionId={module.startSection}
                                                navigateNext={startSectionFinished}
                                            />}
                                        />
                                        <Route
                                            path=":itemId/*"
                                            element={<Section
                                                sectionId={module.startSection}
                                                navigateNext={startSectionFinished}
                                            />}
                                        />
                                    </Route>

                                    <Route
                                        path="slots"
                                        element={<SlotOverview
                                            moduleData={module}
                                            navigateBack={() => navigate('start')}
                                            navigateNext={() => navigate('end')}
                                        />}
                                    />

                                    <Route path="end">
                                        <Route
                                            path=""
                                            element={<Section
                                                sectionId={module.endSection}
                                                navigateNext={endSectionFinished}
                                                showDoneButtonAfterSection={true}
                                            />}
                                        />
                                        <Route
                                            path=":itemId/*"
                                            element={<Section
                                                sectionId={module.endSection}
                                                navigateNext={endSectionFinished}
                                                showDoneButtonAfterSection={true}
                                            />}
                                        />
                                    </Route>
                                </Routes>
                            </div>
                        </div>
                    }
                />
            </Routes>
        );
    } else {
        title = 'Modul';
        content = (
            <div className="content module">
                <TopBar
                    title={title}
                    leftButtons={leftTopBarButtons(`/modules?scrollTo=${moduleId}`)}
                />

                <div className="content-container content-module">
                    <LoadingScreen message="Lade Modul...  Wenn das Modul nicht lädt, starte bitte die App einmal neu." />
                </div>
            </div>
        );
    }

    return content;
};

export default Module;
